import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Select,
  Input,
  Tag,
  Dropdown,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FilterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
  DownOutlined,
  PrinterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  GET_GEO_ALL,
  HEADERS_EXPORT_BEN,
  POST_MAIN_ENFORCEMENT,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import useLawyerNamesNew from "../../../hook/UseLawyerNameNew";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import UpdateEnforceDoc from "./modal/AcceptCaseModal";
import {
  getAssetTypeMeta,
  getCaseTypeLabel,
  getCaseTypeMeta,
  getDistanceZone,
  getStageKey,
  getStageMeta,
  OPTIONS_STAGE,
  OPTIONS_STAGE_WITH_ALL,
  OPTIONS_ZONE,
} from "./constant/ConstantCustom";

const { Text } = Typography;

const formatThaiDate = (d) =>
  d ? dayjs(d).add(543, "year").format("DD/MM/YYYY") : "-";

const formatDateExport = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "-");

const Main = () => {
  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [, , currencyFormatPoint] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const { nameLawyers, loadingUseLawyerNames } = useLawyerNamesNew({
    auto: true,
    toast: false,
  });

  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [stageFilter, setStageFilter] = useState("all");
  const [searchContno, setSearchContno] = useState("");
  const [expMin, setExpMin] = useState(null);
  const [expMax, setExpMax] = useState(null);
  const [lawyerName, setLawyerName] = useState(undefined);
  const [zoneFilter, setZoneFilter] = useState(undefined);
  const [courtFilter, setCourtFilter] = useState(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [geo, setGeo] = useState({
    province: [],
    district: [],
    subDistrict: [],
  });
  const [dataRecord, setDataRecord] = useState(null);
  const [isModal, setIsModal] = useState(false);

  const getRowKey = useCallback(
    (r) => r.latest?.idAssignment ?? `${r.newContno}_${r.idJudge}`,
    [],
  );

  useEffect(() => {
    let isMounted = true;
    const loadGeoData = async () => {
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
        });
        const d = res.data || {};
        if (isMounted) {
          setGeo({
            province: d.province || [],
            district: d.district || [],
            subDistrict: d.subDistrict || [],
          });
        }
      } catch (error) {
        if (error.name !== "CanceledError" && isMounted) {
          message.error("ไม่สามารถโหลดข้อมูลพื้นที่ได้");
        }
      }
    };
    loadGeoData();
    return () => {
      isMounted = false;
    };
  }, []);

  const lawyerOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (nameLawyers ?? [])
            .filter(
              (l) =>
                (l.ROLE_ID === 3 || l.ROLE_ID === 4) &&
                l.ACTIVE_STATUS &&
                l.COMPANY_ID !== 3,
            )
            .map((l) => l.NNAME)
            .filter(Boolean),
        ),
      ).map((name) => ({ value: name, label: name })),
    [nameLawyers],
  );

  const courtOptions = useMemo(() => {
    const base = lawyerName
      ? arrayPay.filter((r) => r.receiveByName === lawyerName)
      : arrayPay;

    const countByCourt = new Map();
    base.forEach((r) => {
      if (!r.provincialCourt) return;
      countByCourt.set(
        r.provincialCourt,
        (countByCourt.get(r.provincialCourt) || 0) + 1,
      );
    });

    return Array.from(countByCourt.entries())
      .sort(([a], [b]) => a.localeCompare(b, "th"))
      .map(([court, count]) => ({
        value: court,
        label: `${court} (${count})`,
      }));
  }, [arrayPay, lawyerName]);

  // ── Geo lookup (เทียบรหัสจังหวัด/อำเภอ/ตำบลของทรัพย์กับข้อมูล geo) ──
  const getProvinceDesc = useCallback(
    (provCode) => {
      if (provCode == null || provCode === "") return "-";
      const p = geo.province.find(
        (x) => String(x.provCode) === String(provCode),
      );
      return p?.provDesc || String(provCode);
    },
    [geo.province],
  );

  const getDistrictDesc = useCallback(
    (distCode, provCode) => {
      if (distCode == null || distCode === "") return "-";
      const d = geo.district.find(
        (x) =>
          String(x.distCode) === String(distCode) &&
          (provCode == null || String(x.provCode) === String(provCode)),
      );
      return d?.distDesc || String(distCode);
    },
    [geo.district],
  );

  const getSubDistrictDesc = useCallback(
    (subDistCode, distCode, provCode) => {
      if (subDistCode == null || subDistCode === "") return "-";
      const s = geo.subDistrict.find(
        (x) =>
          String(x.subDistCode) === String(subDistCode) &&
          (distCode == null || String(x.distCode) === String(distCode)) &&
          (provCode == null || String(x.provCode) === String(provCode)),
      );
      return s?.subDistDesc || String(subDistCode);
    },
    [geo.subDistrict],
  );

  const getZipcode = useCallback(
    (subDistCode, distCode, provCode) => {
      const s = geo.subDistrict.find(
        (x) =>
          String(x.subDistCode) === String(subDistCode) &&
          (distCode == null || String(x.distCode) === String(distCode)) &&
          (provCode == null || String(x.provCode) === String(provCode)),
      );
      return s?.zipcode || null;
    },
    [geo.subDistrict],
  );

  // รวมข้อความที่อยู่ทรัพย์ (จังหวัด/อำเภอ/ตำบล/รหัสไปรษณีย์) จากรหัสที่ทรัพย์เก็บไว้
  const getAssetLocationText = useCallback(
    (a) => {
      const zipcode = getZipcode(a.subdistrict, a.district, a.province);
      return `${getProvinceDesc(a.province)} ${getDistrictDesc(a.district, a.province)} ${getSubDistrictDesc(a.subdistrict, a.district, a.province)}${zipcode ? ` ${zipcode}` : ""}`;
    },
    [getZipcode, getProvinceDesc, getDistrictDesc, getSubDistrictDesc],
  );

  const getAssetDetailText = useCallback(
    (a) => {
      const meta = getAssetTypeMeta(a.typeAsset);
      if (meta.isLand) {
        return `${meta.label}: เลขที่ดิน ${a.numberLand || "-"} ・ ${getAssetLocationText(a)}`;
      }
      if (meta.isVehicle) {
        return `${meta.label}: ${`${a.vhType || ""} ${a.vhModel || ""}`.trim() || "-"} สี${a.vhColor || "-"} ・ ทะเบียน ${a.vhLicensePlate || "-"} ・ เลขตัวถัง ${a.vhChassisNo || "-"} ・ ${getProvinceDesc(a.vhRegProvince)}`;
      }
      return `${meta.label}${a.assetValue != null ? `: ${a.assetValue}` : ""}`;
    },
    [getAssetLocationText, getProvinceDesc],
  );

  const buildAssetRemark = useCallback(
    (r) => (r.assets || []).map(getAssetDetailText).join(" | ") || "-",
    [getAssetDetailText],
  );

  // ── Query ─────────────────────────────────────────────
  const queryData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_MAIN_ENFORCEMENT,
        { userId: userId === 4 ? 1 : userId },
        { headers: HEADERS_EXPORT_BEN },
      );
      const data = Array.isArray(res.data.data) ? res.data.data : [];

      setArrayPay(data);
      setFilteredData(data);
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  useEffect(() => {
    queryData();
  }, []);

  // ── Filter (มีผลเฉพาะตอนกดปุ่ม "กรองข้อมูล") ──────────
  const handleFilter = useCallback(() => {
    let filtered = arrayPay;

    if (stageFilter !== "all") {
      filtered = filtered.filter((d) => getStageKey(d) === stageFilter);
    }
    if (lawyerName) {
      filtered = filtered.filter((d) => d.receiveByName === lawyerName);
    }
    if (expMin != null) {
      filtered = filtered.filter((d) => (d.snapExpPrd ?? 0) >= expMin);
    }
    if (expMax != null) {
      filtered = filtered.filter((d) => (d.snapExpPrd ?? 0) <= expMax);
    }
    if (zoneFilter != null) {
      filtered = filtered.filter(
        (d) => getDistanceZone(d.distance) === zoneFilter,
      );
    }
    if (courtFilter) {
      filtered = filtered.filter((d) => d.provincialCourt === courtFilter);
    }

    const query = searchContno.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (d) =>
          d.newContno?.toLowerCase().includes(query) ||
          d.CUSCOD?.toLowerCase().includes(query),
      );
    }

    setFilteredData(filtered);
    setSelectedRowKeys([]);
  }, [
    arrayPay,
    stageFilter,
    lawyerName,
    expMin,
    expMax,
    zoneFilter,
    courtFilter,
    searchContno,
  ]);

  const hasActiveFilters =
    stageFilter !== "all" ||
    !!lawyerName ||
    expMin != null ||
    expMax != null ||
    zoneFilter != null ||
    !!courtFilter ||
    !!searchContno.trim();

  const handleResetFilters = useCallback(() => {
    setStageFilter("all");
    setSearchContno("");
    setExpMin(null);
    setExpMax(null);
    setLawyerName(undefined);
    setZoneFilter(undefined);
    setCourtFilter(undefined);
    setFilteredData(arrayPay);
    setSelectedRowKeys([]);
  }, [arrayPay]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "รายละเอียด",
      key: "detail",
      align: "left",
      width: 240,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <Text copyable={r.newContno} strong>
              เลขสัญญา {r.newContno || "-"}
            </Text>
          </div>
          <div>
            {`${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-"}
          </div>
          <div style={{ color: "#888" }}>เลขประจำตัว {r.CUSCOD || "-"}</div>
          <Space size={4} wrap>
            {/* <Tag color={r.flagLoanNew === 1 ? "gold" : "blue"}>
              {getCaseTypeLabel(r.flagLoanNew, r.flagDue)}
            </Tag> */}
            {r.round != null && <Tag>รอบที่ {r.round}</Tag>}
          </Space>
        </div>
      ),
    },
    {
      title: "การมอบหมายงาน",
      key: "assignment",
      align: "left",
      width: 230,
      render: (_, r) => {
        const stage = getStageMeta(r);
        return (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <Tag color={stage.color}>{r.stageText || stage.label}</Tag>
            <div>
              มอบหมายให้ {"→ "}
              <Text strong>{r.receiveByName || "-"}</Text>
            </div>
            <div style={{ color: "#888" }}>
              มอบหมาย: {formatThaiDate(r.assignDate)}
            </div>
            <div style={{ color: "#888" }}>
              รับงาน: {formatThaiDate(r.receiveDate)}
            </div>
            {r.distance != null && (
              <div style={{ color: "#888" }}>
                ระยะทาง ≈ {getDistanceZone(r.distance)} กม. (โซน {r.distance})
              </div>
            )}
            {(r.cancelDate || r.active === 0) && (
              <div style={{ color: "#cf1322" }}>
                ยกเลิก {formatThaiDate(r.cancelDate)}
                {r.cancelRemark ? ` (${r.cancelRemark})` : ""}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "รายละเอียด",
      key: "redCaseNumber",
      align: "left",
      width: 220,
      render: (_, record) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <span style={{ color: "#888" }}>เลขคดีแดง </span>
            <Text copyable strong>
              {record?.redCaseNumber || "-"}
            </Text>
          </div>
          <div>
            <span style={{ color: "#888" }}>ศาล </span>
            {record?.provincialCourt || "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>วันพิพากษา </span>
            {formatThaiDate(record?.judgeDate)}
          </div>
          ผู้รับผิดชอบ {record?.lawyerName ? record?.lawyerName : "-"}
        </div>
      ),
    },
    {
      title: "สถานะเอกสาร",
      key: "docStatus",
      align: "left",
      width: 200,
      render: (_, record) => {
        const caseType = getCaseTypeMeta(record.flagLoanNew, record.flagDue);
        return (
          <div style={{ fontSize: 13, lineHeight: 2 }}>
            <Tag color={caseType.color}>{caseType.label}</Tag>
            <div>
              <div>
                {record.flagLoanNew === 2 && (
                  <>
                    <Tag color={record.enforceCaseDate ? "green" : "default"}>
                      คำบังคับ
                    </Tag>
                    <span style={{ color: "#888" }}>
                      {formatThaiDate(record.enforceCaseDate)}
                    </span>
                  </>
                )}
              </div>
              <Tag color={record.docDate ? "green" : "default"}>หมายตั้ง</Tag>
              <span style={{ color: "#888" }}>
                {formatThaiDate(record.docDate)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "สรุปยอดหนี้",
      key: "snapshot",
      align: "left",
      width: 230,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <div>
            <span style={{ color: "#888" }}>ยอดหนี้ตามคำพิพากษา </span>
            {currencyFormatPoint(r.snapTotalJudge) ?? "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>เงินต้นคงเหลือ </span>
            {currencyFormatPoint(r.snapTonBalance) ?? "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>ยอดค้างชำระ </span>
            {currencyFormatPoint(r.snapExpAmt) ?? "-"}
          </div>
          <div>
            <span style={{ color: "#888" }}>เบี้ยปรับคงเหลือ </span>
            <Text
              strong
              style={{ color: r.snapResBeeypup > 0 ? "#cf1322" : "#389e0d" }}
            >
              {currencyFormatPoint(r.snapResBeeypup) ?? "-"}
            </Text>
          </div>
          <div style={{ color: "#888" }}>
            ค้างงวด: จริง {r.snapHldno ?? "-"}
          </div>
          <div style={{ color: "#888" }}>
            ขาดการติดต่อ: {r.snapLostDay ?? "-"} วัน
          </div>
        </div>
      ),
    },
    {
      title: "ทรัพย์ที่พบ",
      key: "assets",
      align: "center",
      width: 120,
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={r.snapFoundAsset ? "green" : "default"}>
            {r.snapFoundAsset ? "พบทรัพย์" : "ยังไม่พบทรัพย์"}
          </Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.assets?.length || 0} รายการ
          </Text>
        </Space>
      ),
    },

    {
      title: "การจัดการ",
      align: "center",
      width: 120,
      render: (_, r) => (
        <>
          <Button
            // icon={<FileAddOutlined />}
            style={{ color: !r.receiveDate ? "red" : "green" }}
            onClick={() => {
              setDataRecord(r);
              setIsModal(true);
            }}
          >
            {!r.receiveDate ? "รอรับงาน" : "รับงานแล้ว"}
          </Button>
        </>
      ),
    },
  ];

  // ── รายละเอียดทรัพย์ (แถวขยาย) ──────────────────────────
  const expandedRowRender = (r) => {
    const assetColumns = [
      {
        title: "ลำดับ",
        key: "i",
        align: "center",
        width: 50,
        render: (_, __, i) => i + 1,
      },
      {
        title: "ประเภท",
        key: "type",
        align: "center",
        width: 150,
        render: (_, a) => (
          <Tag color="cyan">{getAssetTypeMeta(a.typeAsset).label}</Tag>
        ),
      },
      {
        title: "รายละเอียดทรัพย์",
        key: "assetDetail",
        align: "left",
        render: (_, a) => {
          const meta = getAssetTypeMeta(a.typeAsset);
          if (meta.isLand) {
            return (
              <div>
                เลขที่ดิน {a.numberLand || "-"} ・ {getAssetLocationText(a)}
              </div>
            );
          }
          if (meta.isVehicle) {
            return (
              <div>
                {`${a.vhType || ""} ${a.vhModel || ""}`.trim() || "-"} สี
                {a.vhColor || "-"} ・ ทะเบียน {a.vhLicensePlate || "-"} ・
                เลขตัวถัง {a.vhChassisNo || "-"} ・{" "}
                {getProvinceDesc(a.vhRegProvince)}
              </div>
            );
          }
          return (
            <div>
              {a.assetValue != null ? currencyFormatPoint(a.assetValue) : "-"}
            </div>
          );
        },
      },
      {
        title: "ผลลงพื้นที่",
        key: "onsite",
        align: "center",
        width: 160,
        render: (_, a) => (
          <div>
            <Tag color={a.flagOnsite ? "green" : "default"}>
              {a.flagOnsite ? "ลงพื้นที่แล้ว" : "ยังไม่ลงพื้นที่"}
            </Tag>
            {a.dateOnsite && (
              <div style={{ fontSize: 12, color: "#888" }}>
                {formatThaiDate(a.dateOnsite)}
              </div>
            )}
            {a.onsiteResult && (
              <div style={{ fontSize: 12 }}>{a.onsiteResult}</div>
            )}
          </div>
        ),
      },
    ];

    return (
      <Table
        size="small"
        columns={assetColumns}
        dataSource={r.assets || []}
        rowKey={(a) => a.idAsset ?? a.assetKey}
        pagination={false}
        style={{ background: "#fafafa", borderRadius: 8 }}
      />
    );
  };
  // ✅ Reload หลังบันทึกสำเร็จ
  const handleReload = useCallback(() => {
    queryData();
  }, [queryData]);

  // ── Export helpers ────────────────────────────────────
  const selectedRows = useMemo(
    () => filteredData.filter((r) => selectedRowKeys.includes(getRowKey(r))),
    [filteredData, selectedRowKeys, getRowKey],
  );

  // ไม่ได้เลือกแถวใด ๆ → export ข้อมูลที่กรองไว้ทั้งหมดแทน
  const exportRows = selectedRows.length ? selectedRows : filteredData;

  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  // คอลัมน์ตาม columns ของ Table (ลำดับ / รายละเอียด / การมอบหมายงาน / สรุปยอดหนี้ / ทรัพย์ที่พบ)
  const moneyKeys = [
    "ยอดหนี้ตามคำพิพากษา",
    "เงินต้นคงเหลือ",
    "ยอดค้างชำระ",
    "เบี้ยปรับคงเหลือ",
  ];

  const buildExportRows = () =>
    exportRows.map((r, i) => ({
      ลำดับ: i + 1,
      เลขสัญญา: r.newContno || "",
      "ชื่อ-นามสกุล": `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(),
      เลขประจำตัว: r.CUSCOD || "",
      ประเภทคดี: getCaseTypeLabel(r.flagLoanNew, r.flagDue),
      เลขคดีแดง: r.redCaseNumber || "",
      ศาล: r.provincialCourt || "",
      วันพิพากษา: formatDateExport(r.judgeDate),
      ทนายความ: r.lawyerName || "",
      สถานะงาน: r.stageText || "",
      หมายเหตุ: buildAssetRemark(r),
      ผู้รับผิดชอบ: r.receiveByName || "",
      วันที่มอบหมาย: formatDateExport(r.assignDate),
      วันที่รับงาน: formatDateExport(r.receiveDate),
      "ระยะทาง≈ (โซน)":
        r.distance != null
          ? `${getDistanceZone(r.distance)} กม. (${r.distance})`
          : "-",
      ยอดหนี้ตามคำพิพากษา: parseFloat(r.snapTotalJudge) || 0,
      เงินต้นคงเหลือ: parseFloat(r.snapTonBalance) || 0,
      ยอดค้างชำระ: parseFloat(r.snapExpAmt) || 0,
      เบี้ยปรับคงเหลือ: parseFloat(r.snapResBeeypup) || 0,
      "ขาดการติดต่อ (วัน)": r.snapLostDay ?? "",
      จำนวนทรัพย์ที่พบ: r.assets?.length || 0,
    }));

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const summaryRow = {
      ลำดับ: "",
      เลขสัญญา: "",
      "ชื่อ-นามสกุล": `รวมทั้งหมด ${rows.length} รายการ`,
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const evenFill = { patternType: "solid", fgColor: { rgb: "EBF5FF" } };
    const oddFill = { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
    const summaryFill = { patternType: "solid", fgColor: { rgb: "D5E8D4" } };
    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    // header
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows
    rows.forEach((row, ri) => {
      const fill = ri % 2 === 0 ? evenFill : oddFill;
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        const isNumber = typeof row[key] === "number";
        const isMoney = moneyKeys.includes(key);
        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: {
            horizontal: isNumber ? "right" : "left",
            vertical: "center",
          },
          border: borderStyle,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;
      const isMoney = moneyKeys.includes(key);
      ws[cellRef].s = {
        fill: summaryFill,
        font: { bold: true, sz: 12 },
        alignment: {
          horizontal: isMoney ? "right" : "left",
          vertical: "center",
        },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2, 12) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(
      wb,
      `รายงานสัญญาที่มอบหมายบังคับคดี_${dayjs().format("DDMMYYYY")}.xlsx`,
    );
    message.success("Export Excel สำเร็จ");
  };

  const handlePrintPDF = () => {
    if (!exportRows.length) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานสัญญาที่มอบหมายบังคับคดี", pageWidth / 2, 8, {
        align: "center",
      });
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(12);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 16);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 16, { align: "right" });
    };

    const rows = buildExportRows();
    const tableBody = rows.map((r) =>
      Object.entries(r).map(([key, val]) =>
        // format ตัวเลขเงินให้มี comma ใน PDF
        moneyKeys.includes(key) ? currencyFormatPoint(val) : val,
      ),
    );

    doc.autoTable({
      startY: 20,
      head: [Object.keys(rows[0])],
      body: tableBody,
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 10,
      },
      styles: {
        font: "THSarabunNew",
        fontSize: 9, // 17 คอลัมน์ ลดขนาด font ไม่ให้ล้นหน้า
        cellPadding: 1,
        halign: "center",
      },
      columnStyles: {
        2: { halign: "left" }, // ชื่อ-นามสกุล
        6: { halign: "left" }, // หมายเหตุ
        7: { halign: "left" }, // ผู้รับผิดชอบ
        11: { halign: "right" }, // ยอดหนี้ตามคำพิพากษา
        12: { halign: "right" }, // เงินต้นคงเหลือ
        13: { halign: "right" }, // ยอดค้างชำระ
        14: { halign: "right" }, // เบี้ยปรับคงเหลือ
      },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      margin: { top: 20, left: 3, right: 3 },
    });

    doc.save(
      `รายงานสัญญาที่มอบหมายบังคับคดี_${dayjs().format("DDMMYYYY")}.pdf`,
    );
  };

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>
            รายงานสัญญาที่มอบหมายบังคับคดี
          </Divider>

          {/* ── แถบกรองข้อมูล ── */}
          <Card
            size="small"
            title={
              <Space>
                <FilterOutlined />
                <span>ตัวกรองข้อมูล</span>
              </Space>
            }
            style={{ marginBottom: 12, background: "#fafafa" }}
          >
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} md={6} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  สถานะงาน
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={OPTIONS_STAGE_WITH_ALL}
                  value={stageFilter}
                  onChange={setStageFilter}
                  size="large"
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ค้นหาเลขที่สัญญา
                </Text>
                <Input
                  placeholder="เช่น V2-0012430"
                  value={searchContno}
                  onChange={(e) => setSearchContno(e.target.value)}
                  onPressEnter={handleFilter}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  allowClear
                  size="small"
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ผู้รับผิดชอบ
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={lawyerOptions}
                  value={lawyerName}
                  onChange={setLawyerName}
                  loading={loadingUseLawyerNames}
                  allowClear
                  showSearch
                  placeholder="ทั้งหมด"
                  size="large"
                  disabled={roleId > 2}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  โซน
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={OPTIONS_ZONE}
                  value={zoneFilter}
                  onChange={setZoneFilter}
                  allowClear
                  placeholder="ทั้งหมด"
                  size="large"
                  popupMatchSelectWidth={false}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ศาล
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={courtOptions}
                  value={courtFilter}
                  onChange={setCourtFilter}
                  allowClear
                  showSearch
                  placeholder="ทั้งหมด"
                  size="large"
                  popupMatchSelectWidth={false}
                />
              </Col>
              {/*
              <Col xs={24} sm={12} md={6} lg={3}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ประเภท
                </Text>
                <div>
                  <Checkbox.Group
                    options={OPTIONS_CASE_TYPE}
                    value={caseTypeFilter}
                    onChange={setCaseTypeFilter}
                  />
                </div>
              </Col> */}

              <Col flex="auto" style={{ textAlign: "right" }}>
                <Space>
                  <Button
                    color="orange"
                    variant="outlined"
                    icon={<FilterOutlined />}
                    onClick={handleFilter}
                  >
                    กรองข้อมูล
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetFilters}
                    color="red"
                    variant="outlined"
                  >
                    ล้างตัวกรอง
                  </Button>
                  <Dropdown
                    menu={exportMenuItems}
                    disabled={!filteredData.length}
                  >
                    <Button icon={<PrinterOutlined />}>
                      Export <DownOutlined />
                    </Button>
                  </Dropdown>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* ── สรุปตัวกรองที่ใช้อยู่ ── */}
          {hasActiveFilters && (
            <Card
              size="small"
              style={{ background: "#f0f5ff", marginBottom: 12 }}
            >
              <Space split="|" size="large" wrap>
                <span>
                  <strong>ผลการค้นหา:</strong> {filteredData.length} รายการ
                </span>
                {stageFilter !== "all" && (
                  <span>
                    <strong>สถานะงาน:</strong>{" "}
                    {OPTIONS_STAGE.find((o) => o.value === stageFilter)
                      ?.label || stageFilter}
                  </span>
                )}
                {searchContno && (
                  <span>
                    <strong>ค้นหา:</strong> "{searchContno}"
                  </span>
                )}
                {(expMin != null || expMax != null) && (
                  <span>
                    <strong>ค้างงวด:</strong> {expMin ?? 0} - {expMax ?? "∞"}
                  </span>
                )}
                {lawyerName && (
                  <span>
                    <strong>ผู้รับผิดชอบ:</strong>
                    {lawyerName}
                  </span>
                )}
                {zoneFilter != null && (
                  <span>
                    <strong>โซน:</strong>{" "}
                    {OPTIONS_ZONE.find((o) => o.value === zoneFilter)?.label ||
                      zoneFilter}
                  </span>
                )}
                {courtFilter && (
                  <span>
                    <strong>ศาล:</strong> {courtFilter}
                  </span>
                )}
              </Space>
            </Card>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* <Button
              color="blue"
              variant="filled"
              icon={<PlusOutlined />}
              onClick={() => handleBulkSave()}
              disabled={selectedRows.length === 0}
              loading={loading}
              style={{ borderRadius: 8 }}
            >
              ส่งบังคับคดี{" "}
              {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length})`}
            </Button> */}

            <Text
              strong
              style={{
                margin: "10px 0",
              }}
            >
              จำนวนสัญญาทั้งหมด : {arrayPay.length} เคส
              {selectedRowKeys.length > 0
                ? ` (เลือก ${selectedRowKeys.length} รายการ)`
                : " (ไม่เลือกรายการ = Export ข้อมูลทั้งหมด)"}
            </Text>
          </div>
          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={getRowKey}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            expandable={{
              expandedRowRender,
              rowExpandable: (r) => (r.assets?.length || 0) > 0,
            }}
            scroll={{ x: 1100 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
          />
        </Card>
        {isModal && (
          <UpdateEnforceDoc
            open={isModal}
            onCancel={setIsModal}
            dataDefault={dataRecord}
            onSuccess={handleReload}
            geo={geo}
          />
        )}
      </Spin>
    </>
  );
};

const MainContractToEnforce = MotionHoc(Main);
export default MainContractToEnforce;
